<template>
    <div
        :id="id"
        ref="container"
        :data-testid="testId || 'input-dropdown-wrapper'"
        class="input-dropdown-wrapper"
    >
        <div
            ref="dropdownRef"
            class="input-dropdown"
            :class="{
                active: dropdownOpen,
                filled,
                'open-top': dropdownDirection === 'top',
                error,
                disabled,
                checkmark,
                multiline,
            }"
            :style="{ top: dropdownDirection === 'top' ? (-1 * getOptionsHeight() + 'px') : undefined }"
        >
            <div
                ref="labelRef"
                class="label"
                @click="handleDropdownOpen"
            >
                <slot
                    name="dropdown-selected-option"
                    :selected-item-label="getLabel"
                    :selected-item-value="getValue"
                >
                    <div class="option-container">
                        <div
                            v-if="label && label !== getLabel"
                            class="floating-dropdown-label"
                        >
                            {{ label }}
                        </div>
                        <Words
                            :oneline="!multiline"
                            :class="{ 'text-break': filled }"
                        >{{ getLabel }}</Words>
                    </div>
                </slot>
                <span
                    v-if="!disabled"
                    class="icon-container"
                >
                    <slot name="dropdown-icon">
                        <i
                            v-if="filled && !dropdownOpen && !required"
                            class="icon mi-close"
                            :style="dropdownOpen && 'transform: rotate(180deg)'"
                            @click.stop="handleClear"
                        />
                        <i
                            v-else
                            class="icon mi-chevron-down"
                            :style="dropdownOpen && 'transform: rotate(180deg)'"
                            @click="handleDropdownClose($event, true)"
                        />
                    </slot>
                </span>
            </div>
            <transition
                name="input-dropdown"
                mode="out-in"
            >
                <div
                    v-if="dropdownOpen"
                    class="dropdown"
                    data-testid="input-dropdown-options"
                >
                    <div class="dropdown-inner">
                        <label
                            v-if="isSearchable"
                            class="item search-input-wrapper"
                        >
                            <div class="label-input-wrapper">
                                <div>
                                    <input
                                        ref="searchRef"
                                        v-model="query"
                                        type="search"
                                        :placeholder="t('search')"
                                    />
                                </div>
                            </div>
                        </label>

                        <div
                            v-for="item in filteredItems"
                            :key="item[keyField ? keyField : 'value']"
                            class="scroller"
                        >
                            <div
                                class="item"
                                :class="{ active: isActive(item) }"
                                data-testid="input-dropdown-options-item"
                                @click="handleSelect(item)"
                            >
                                <slot
                                    name="dropdown-option"
                                    :item="item"
                                >
                                    <Position
                                        v-if="checkmark"
                                        x="center"
                                        y="center"
                                        class="dropdown-checkmark"
                                    >
                                        <CheckTrigger :model-value="isActive(item)" />
                                    </Position>
                                    <Words
                                        :oneline="!multiline"
                                        class="text-break"
                                    >{{ itemLabel ? itemLabel(item) : item }}</Words>
                                </slot>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
        </div>

        <ErrorText
            v-if="error && !dropdownOpen"
            :error="error"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue'
import ErrorText from 'widget-components/components/ErrorText'
import { onClickOutside, type OnClickOutsideHandler } from '@vueuse/core'
import isIframe from 'widget-components/utilities/isIframe'
import { useI18n } from 'widget-components/utilities/i18n'
import { clamp, isNil } from 'ramda'
import { clone } from 'widget-components/utilities/clone'
import equals from 'fast-deep-equal/es6'
import Position from '../Position/Position.vue'
import CheckTrigger from '../InputCheckbox/CheckTrigger.vue'
import type { InputDropdownItem, InputDropdownProps } from './types'
import Words from 'widget-components/components/Words'
import { type BlockPath } from 'widget-components/integrations/CustomPage/types'
import { useSpaceAvailability } from 'widget-components/composables/space-availability'

const ITEM_SIZE = 60

const container = useTemplateRef<HTMLDivElement>('container')
const labelRef = ref<HTMLDivElement>()
const dropdownRef = ref<HTMLDivElement>()
const searchRef = ref<HTMLDivElement>()

defineOptions({ inheritAttrs: false })

const props = defineProps<InputDropdownProps & {
    blockPath?: BlockPath
    required?: boolean
}>()

const emit = defineEmits(['update:modelValue', 'clear', 'dropdown-open'])

const query = ref('')
const dropdownOpen = ref(false)
const model = ref()
const dropdownDirection = ref<string>('bottom')

const filled = computed(() => props.multiple ? model.value?.length : !!model.value)

const isSearchable = computed(() => {
    return (props.items || []).length > 5
})

const { spaceToBottom } = useSpaceAvailability(container)
const getDropdownDirection = () => getOptionsHeight() > spaceToBottom.value ? 'top' : 'bottom'

const { t } = useI18n()

const filteredItems = computed(() => {
    if (isSearchable.value && query.value) {
        const queryValue = query.value.toLowerCase()

        return props.items.filter((el) => {
            const label = props.itemLabel(el)?.toLowerCase()
            const value = props.itemValue(el)?.toLowerCase()

            return label?.includes(queryValue) || value?.includes(queryValue)
        })
    }

    return props.items
})

const getOptionsHeight = () => clamp(0, 4, filteredItems.value.length + (isSearchable.value ? 1 : 0)) * ITEM_SIZE

const dropdownMaxHeight = computed(() => {
    const inputHeight = typeof window !== 'undefined' && container.value && parseInt(window.getComputedStyle(container.value).getPropertyValue('--tl-inputs-height'))
    return `${getOptionsHeight() + (inputHeight || ITEM_SIZE) + 2}px`
})

const selectedOptions = computed(() => {
    if (props.multiple) {
        return [...(model.value as InputDropdownItem[]).map((el) => props.itemLabel(el))].join(', ')
    }
    return model.value ? props.itemLabel(model.value) : ''
})

const selectedOptionsValue = computed(() => {
    if (props.multiple) {
        return [...(model.value as InputDropdownItem[]).map((el) => props.itemValue(el))].join(', ')
    }
    return model.value ? props.itemValue(model.value) : ''
})

const getLabel = computed(() => {
    if (props.multiple) {
        return model.value?.length ? selectedOptions.value : props.label
    }

    return model.value ? selectedOptions.value : props.label
})

const getValue = computed(() => {
    if (props.multiple) {
        return model.value?.length ? selectedOptionsValue.value : ''
    }

    return model.value ? selectedOptionsValue.value : ''
})

const isActive = (item: InputDropdownItem) => {
    if (props.multiple) {
        return !!(model.value as InputDropdownItem[]).find((el) => props.itemValue(el) === props.itemValue(item))
    }

    return model.value && props.itemValue(model.value) === props.itemValue(item)
}

const handleMultiselect = (item: InputDropdownItem) => {
    const exists = (model.value as InputDropdownItem[]).find((el) => props.itemValue(el) === props.itemValue(item))

    if (exists) {
        model.value = (model.value as InputDropdownItem[]).filter((el) => props.itemValue(el) !== props.itemValue(item))
    } else {
        model.value = [...model.value, item]
    }
}

const handleSelect = (item: InputDropdownItem) => {
    if (props.multiple) {
        handleMultiselect(item)
    } else {
        model.value = item
        handleDropdownClose()
    }
}

let stopClickOutsideVueUse: () => void
const stopClickOutside = () => {
    if (stopClickOutsideVueUse && typeof stopClickOutsideVueUse === 'function') {
        stopClickOutsideVueUse()
    }
    window.removeEventListener('blur', handleDropdownCloseEvent)
    document.removeEventListener('keydown', handleEscKey, { capture: true })
}

onUnmounted(() => {
    stopClickOutside()
})

const handleEscKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && dropdownOpen.value) {
        event.stopPropagation()
        handleDropdownClose(event, true)
    }
}

const handleDropdownOpen = () => {
    if (props.disabled) {
        return
    }

    dropdownOpen.value = !dropdownOpen.value
    dropdownDirection.value = getDropdownDirection()

    if (dropdownOpen.value) {
        emit('dropdown-open')
        stopClickOutsideVueUse = onClickOutside(container, handleDropdownClose as OnClickOutsideHandler, { detectIframe: true })

        if (isIframe) {
            window.addEventListener('blur', handleDropdownCloseEvent, { passive: true })
        }

        document.addEventListener('keydown', handleEscKey, { capture: true })

        nextTick(() => {
            if (searchRef.value) {
                searchRef.value.focus()
            }
        })
    } else {
        stopClickOutside()
    }
}

const handleDropdownCloseEvent = (event: Event) => {
    handleDropdownClose(event, false)
}

const handleDropdownClose = (event?: Event, stop?: boolean) => {
    if (!dropdownOpen.value) {
        return
    }

    if (stop) {
        event?.stopPropagation()
    }

    dropdownOpen.value = false
    query.value = ''

    stopClickOutside()
}

const handleClear = () => {
    if (props.disabled) {
        return
    }

    model.value = props.multiple ? [] : null

    emit('clear')
}

watch(
    () => props.multiple,
    () => {
        if (props.multiple) {
            model.value = (props.modelValue as InputDropdownItem[]) instanceof Array ? props.modelValue : []
        } else {
            model.value = props.modelValue
        }
    },
    { immediate: true },
)

const handleInitialValue = () => {
    if (isNil(props.initialValue)) {
        return
    }

    if (props.multiple) {
        const initialValues = (Array.isArray(props.initialValue) ? props.initialValue :
            [props.initialValue]).map(i => props.itemValue?.(i))
        const items = props.items?.filter((el) => initialValues.includes(props.itemValue?.(el))) ?? []
        model.value = clone(items)
    } else {
        const initialValue = props.itemValue?.(props.initialValue)
        const item = props.items?.find((el) => props.itemValue?.(el) === initialValue)
        model.value = clone(item) || props.label
    }
}

onMounted(() => {
    handleInitialValue()
})

watch(() => props.initialValue, (val, prev) => {
    if (!equals(val, prev)) {
        handleInitialValue()
    }
})

watch(model, (val) => {
    emit('update:modelValue', val)
})

watch(() => props.items, (val, prev) => {
    if (!equals(val, prev)) {
        dropdownDirection.value = getDropdownDirection()
        nextTick(() => {
            if (dropdownOpen.value && isSearchable.value && searchRef.value) {
                searchRef.value.focus()
            }
        })
    }
})

</script>

<style lang="sass" scoped>
@import './InputDropdown.sass'

.input-dropdown.active
    max-height: v-bind(dropdownMaxHeight)
</style>
